import { Button, Card, CardBody } from '@heroui/react';
import React, { useState } from 'react';

import { BuyerChatsModal } from '../BuyerChats/BuyerChatsModal';

import { ConfirmRemoveBuyerModal } from './ConfirmRemoveBuyerModal';
import { RoleSelect } from './RoleSelect';
import { BuyerSelectOption } from './types';

interface SelectedBuyersListProps {
  buyers: BuyerSelectOption[];
  onDeleteBuyer: (buyer: BuyerSelectOption) => void;
  onBuyerUserRoleChange: (newBuyer: BuyerSelectOption) => void;
  userName: string;
  userId: string;
  disabled?: boolean;
}

export const SelectedBuyersList: React.FC<SelectedBuyersListProps> = ({
  buyers,
  onDeleteBuyer,
  onBuyerUserRoleChange,
  userName,
  userId,
  disabled = false,
}) => {
  const [buyerToRemove, setBuyerToRemove] = useState<BuyerSelectOption | null>(
    null
  );
  const [buyerForChats, setBuyerForChats] = useState<BuyerSelectOption | null>(
    null
  );

  const handleRemoveClick = (buyer: BuyerSelectOption) => {
    setBuyerToRemove(buyer);
  };

  const handleConfirmRemove = () => {
    if (buyerToRemove) {
      onDeleteBuyer(buyerToRemove);
      setBuyerToRemove(null);
    }
  };

  const handleCloseModal = () => {
    setBuyerToRemove(null);
  };

  if (buyers.length === 0) {
    return null;
  }

  return (
    <>
      <div className="space-y-3 mt-4">
        {buyers.map((buyer) => (
          <Card key={buyer.value} className="shadow-sm">
            <CardBody className="p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-default-900 truncate">
                    {buyer.label}
                  </p>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {buyer.metaData?.rolesEnabled && (
                    <RoleSelect
                      buyer={buyer}
                      onRoleChange={onBuyerUserRoleChange}
                      disabled={disabled}
                    />
                  )}

                  <Button
                    variant="light"
                    color="primary"
                    size="sm"
                    onPress={() => setBuyerForChats(buyer)}
                    isDisabled={disabled || !userId}
                    data-testid={`manage-chats-${buyer.value}`}
                    aria-label={`Manage chats for ${buyer.label}`}
                  >
                    Manage Chats
                  </Button>

                  <Button
                    isIconOnly
                    variant="light"
                    color="danger"
                    size="sm"
                    onPress={() => handleRemoveClick(buyer)}
                    isDisabled={disabled}
                    data-testid={`remove-buyer-${buyer.value}`}
                    aria-label={`Remove ${buyer.label}`}
                  >
                    ×
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      <ConfirmRemoveBuyerModal
        isOpen={!!buyerToRemove}
        onClose={handleCloseModal}
        onConfirm={handleConfirmRemove}
        userName={userName}
        buyerName={buyerToRemove?.label || ''}
      />

      {buyerForChats && (
        <BuyerChatsModal
          isOpen={!!buyerForChats}
          onClose={() => setBuyerForChats(null)}
          buyerId={buyerForChats.value}
          userId={userId}
          userName={userName}
          buyerName={buyerForChats.label}
        />
      )}
    </>
  );
};
